import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCircle, Clock, AlertCircle, Plus, Mail, MessageSquare, Calendar, Trash2, History, Settings } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { Reminder, ReminderStatus, ReminderPriority, ReminderLog, DeliveryMethod } from '../../types/reminder';
import CreateReminderModal from './CreateReminderModal';
import EditReminderModal from './EditReminderModal';
import DeleteReminderModal from './DeleteReminderModal';
import { parseLocalDate } from '../../utils/dateUtils';

// Form reminder type placeholder ID used by backend
const FORM_REMINDER_TYPE_ID = '00000000-0000-0000-0000-000000000001';

// Immunization reminder type - configured globally on vaccination protocols
const IMMUNIZATION_REMINDER_TYPE_ID = '00000000-0000-0000-0000-000000000003';

// Helper to detect if a reminder is an immunization reminder by name
const isImmunizationReminder = (reminder: Reminder): boolean => {
  const typeName = reminder.reminder_type?.name?.toLowerCase() || '';
  const title = reminder.title?.toLowerCase() || '';
  
  return (
    reminder.reminder_type_id === IMMUNIZATION_REMINDER_TYPE_ID ||
    typeName.includes('immunization') ||
    typeName.includes('vaccine') ||
    typeName.includes('vaccination') ||
    // Common vaccine names
    title.includes('rabies') ||
    title.includes('dhpp') ||
    title.includes('distemper') ||
    title.includes('parvo') ||
    title.includes('bordetella') ||
    title.includes('leptospirosis') ||
    title.includes('fvrcp') ||
    title.includes('felv')
  );
};

interface PetRemindersSectionProps {
  petId: string;
  petName?: string;
}

const PetRemindersSection: React.FC<PetRemindersSectionProps> = ({ petId, petName }) => {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [reminderLogs, setReminderLogs] = useState<ReminderLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedReminder, setExpandedReminder] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [reminderToEdit, setReminderToEdit] = useState<Reminder | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reminderToDelete, setReminderToDelete] = useState<Reminder | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchReminders();
    fetchReminderLogs();
  }, [petId]);

  const fetchReminders = async () => {
    try {
      const token = localStorage.getItem('access_token');
      // Add timestamp to bust any caching
      const url = `${API_ENDPOINTS.REMINDERS.BY_PET(petId)}?_t=${Date.now()}`;
      console.log('🔍 Fetching reminders from:', url);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });
      console.log('📡 Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Reminders API returned:', data);
        console.log('📊 Total count:', Array.isArray(data) ? data.length : 0);
        const remindersList = Array.isArray(data) ? data : [];

        // Debug each reminder
        remindersList.forEach((r: any, idx: number) => {
          console.log(`📋 Reminder ${idx + 1}:`, {
            id: r.id,
            status: r.status,
            due_date: r.due_date,
            reminder_type: r.reminder_type?.name,
            scheduled_send_time: r.scheduled_send_time
          });
        });

        setReminders(remindersList);
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch reminders:', response.status, errorText);
      }
    } catch (err) {
      console.error('💥 Error fetching reminders:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReminderLogs = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const url = API_ENDPOINTS.REMINDERS.BY_PET_LOGS(petId);
      console.log('📜 Fetching reminder logs from:', url);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('📜 Reminder logs response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('📜 Reminder logs data:', data);
        console.log('📜 Reminder logs count:', Array.isArray(data) ? data.length : 0);
        setReminderLogs(Array.isArray(data) ? data : []);
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch reminder logs:', response.status, errorText);
      }
    } catch (err) {
      console.error('💥 Error fetching reminder logs:', err);
    }
  };

  const handleEditClick = (reminder: Reminder) => {
    // If it's a form reminder, navigate to forms settings instead of edit modal
    if (reminder.reminder_type_id === FORM_REMINDER_TYPE_ID || reminder.reminder_type?.name === 'Form') {
      navigate('/settings/forms');
      return;
    }
    
    // If it's an immunization reminder, navigate to vaccination protocols settings
    // Immunization reminders are now configured globally on the vaccination protocol
    if (isImmunizationReminder(reminder)) {
      navigate('/settings/immunization-catalog?tab=templates');
      return;
    }
    
    setReminderToEdit(reminder);
    setShowEditModal(true);
  };

  const handleDeleteClick = (reminder: Reminder, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the edit modal
    setReminderToDelete(reminder);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reminderToDelete) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Use source_type from backend to determine the correct delete endpoint
      // This prevents 404 errors when a regular reminder has a vaccine-related name
      let endpoint: string;
      
      if (reminderToDelete.source_type === 'form_trigger') {
        endpoint = API_ENDPOINTS.REMINDERS.DELETE_FORM_TRIGGER_INSTANCE(reminderToDelete.id);
      } else if (reminderToDelete.source_type === 'immunization_reminder') {
        endpoint = API_ENDPOINTS.REMINDERS.DELETE_IMMUNIZATION_REMINDER_INSTANCE(reminderToDelete.id);
      } else {
        // Default: "regular" or undefined (for backward compatibility)
        endpoint = API_ENDPOINTS.REMINDERS.CANCEL(reminderToDelete.id);
      }
      
      console.log(`🗑️ Deleting reminder with source_type=${reminderToDelete.source_type}, endpoint=${endpoint}`);
      
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        // Refresh the list
        await fetchReminders();
        setShowDeleteModal(false);
        setReminderToDelete(null);
      } else {
        window.alert('Failed to delete reminder');
      }
    } catch (err) {
      console.error('Failed to delete reminder:', err);
      window.alert('Failed to delete reminder');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setReminderToDelete(null);
  };

  const getStatusIcon = (status: ReminderStatus) => {
    switch (status) {
      case ReminderStatus.COMPLETED:
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case ReminderStatus.SENT:
        return <Mail className="w-4 h-4 text-blue-600" />;
      case ReminderStatus.PENDING:
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case ReminderStatus.CANCELLED:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
      case ReminderStatus.FAILED:
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Bell className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: ReminderStatus) => {
    switch (status) {
      case ReminderStatus.COMPLETED:
        return 'text-green-600 bg-green-50 border-green-200';
      case ReminderStatus.SENT:
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case ReminderStatus.PENDING:
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case ReminderStatus.CANCELLED:
        return 'text-gray-600 bg-gray-50 border-gray-200';
      case ReminderStatus.FAILED:
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getDeliveryMethodIcon = (method: DeliveryMethod) => {
    switch (method) {
      case DeliveryMethod.EMAIL:
        return <Mail className="w-3.5 h-3.5" />;
      case DeliveryMethod.SMS:
        return <MessageSquare className="w-3.5 h-3.5" />;
      default:
        return <Bell className="w-3.5 h-3.5" />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = parseLocalDate(dateString);
    const now = new Date();
    // Reset both to start of day for accurate day comparison
    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffTime = dateStart.getTime() - nowStart.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return `${Math.abs(diffDays)} days overdue`;
    } else if (diffDays === 0) {
      return 'Due today';
    } else if (diffDays === 1) {
      return 'Due tomorrow';
    } else if (diffDays <= 7) {
      return `Due in ${diffDays} days`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Simple filtering: pending vs completed
  const pendingReminders = reminders.filter(r =>
    r.status === ReminderStatus.PENDING
  );
  const completedReminders = reminders.filter(r =>
    r.status === ReminderStatus.COMPLETED || r.status === ReminderStatus.SENT || r.status === ReminderStatus.CANCELLED
  );

  // Debug logging
  useEffect(() => {
    console.log('🔍 Reminders:');
    console.log('  Total:', reminders.length);
    console.log('  Pending:', pendingReminders.length);
    console.log('  Completed:', completedReminders.length);

    reminders.forEach((r, idx) => {
      console.log(`  Reminder ${idx + 1}:`, {
        status: r.status,
        due_date: r.due_date,
        type: r.reminder_type?.name
      });
    });
  }, [reminders, pendingReminders, completedReminders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Bell className="w-5 h-5 text-blue-600" />
          Reminders
        </h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors bg-white dark:bg-dark-bg-tertiary px-3 py-1.5 rounded-none border border-blue-100 dark:border-blue-800 shadow-sm hover:shadow-md"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>

      <div className="p-6">

        {pendingReminders.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <Bell className="w-8 h-8 mx-auto mb-1.5 text-gray-300" />
            <p className="text-xs">No pending reminders</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Pending Reminders */}
            {pendingReminders.map((reminder) => {
              const isImmunization = isImmunizationReminder(reminder);
              const isForm = reminder.reminder_type_id === FORM_REMINDER_TYPE_ID || reminder.reminder_type?.name === 'Form';
              const isGlobal = isImmunization || isForm;
              
              return (
                <div
                  key={reminder.id}
                  onClick={() => handleEditClick(reminder)}
                  className="p-4 border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary rounded-xl cursor-pointer group shadow-sm transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:scale-[1.01] hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-dark-bg-hover"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900 dark:text-dark-text-primary truncate">
                          {reminder.title || reminder.reminder_type?.name || 'Reminder'}
                        </span>
                        {isGlobal && (
                          <span 
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full"
                            title={isImmunization ? 'Configure in Vaccination Protocols settings' : 'Configure in Forms settings'}
                          >
                            <Settings className="w-3 h-3" />
                            Global
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-dark-text-muted">
                        <Calendar className="w-4 h-4" />
                        {reminder.due_date ? formatDate(reminder.due_date) : '-'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={(e) => handleDeleteClick(reminder, e)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-none transition-colors opacity-0 group-hover:opacity-100"
                        title="Delete reminder"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create Reminder Modal */}
        <CreateReminderModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchReminders();
            fetchReminderLogs();
          }}
          petId={petId}
          petName={petName}
        />

        {/* Edit Reminder Modal */}
        {reminderToEdit && (
          <EditReminderModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setReminderToEdit(null);
            }}
            onSuccess={() => {
              setShowEditModal(false);
              setReminderToEdit(null);
              fetchReminders();
              fetchReminderLogs();
            }}
            reminder={reminderToEdit}
            petName={petName}
          />
        )}

        {/* Delete Reminder Modal */}
        <DeleteReminderModal
          isOpen={showDeleteModal}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          reminderName={reminderToDelete?.title || reminderToDelete?.reminder_type?.name || 'this reminder'}
          loading={deleting}
        />
      </div>
    </>
  );
};

export default PetRemindersSection;

